import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import { AdminArticlesPage } from '@/components/admin/articles-page';
import { adminHead } from '@/lib/seo';
import {
    adminListArticlesFn,
    adminListAuthorsFn,
    adminListCategoriesFn,
} from '@/server/functions/admin';

const articlesSearchSchema = z.object({
    status: z.enum(['all', 'published', 'unpublished']).optional(),
});

export const Route = createFileRoute('/_admin/admin/articles')({
    component: ArticlesRoute,
    head: () => adminHead('כתבות', '/admin/articles'),
    validateSearch: (search) => articlesSearchSchema.parse(search),
    staleTime: 0,
    preloadStaleTime: 0,
    loaderDeps: ({ search }) => ({
        status: search.status ?? 'all',
    }),
    loader: async ({ deps }) => {
        const [articles, categories, authors] = await Promise.all([
            adminListArticlesFn({
                data: {
                    limit: 50,
                    status:
                        deps.status !== 'all' ? deps.status : undefined,
                },
            }),
            adminListCategoriesFn(),
            adminListAuthorsFn({ data: { limit: 200 } }),
        ]);
        return {
            articles,
            categories: categories.rows,
            authors: authors.rows,
        };
    },
});

function ArticlesRoute() {
    const { articles, categories, authors } = Route.useLoaderData();
    const { status: searchStatus } = Route.useSearch();
    const navigate = useNavigate({ from: Route.fullPath });
    const status = searchStatus ?? 'all';

    return (
        <AdminArticlesPage
            initial={articles}
            categories={categories}
            authors={authors}
            status={status}
            onStatusChange={(next) => {
                void navigate({
                    search: (prev) => ({
                        ...prev,
                        status: next === 'all' ? undefined : next,
                    }),
                    replace: true,
                });
            }}
        />
    );
}
